import { useEffect, useRef, useState } from "react";
import { useShell } from "./ShellFrame";
import { useLocation, useNavigate } from "react-router-dom";
import { Bell, Search, Sun, Moon, ChevronDown, Command, Loader2, User, CalendarCheck, BedDouble, Receipt, FileText, X } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";

const THEME_KEY = "hocp.theme";

const TYPE_META = {
  guest:       { icon: User,          label: "Guest" },
  reservation: { icon: CalendarCheck, label: "Reservation" },
  stay:        { icon: BedDouble,     label: "Stay" },
  folio:       { icon: Receipt,       label: "Folio" },
  invoice:     { icon: FileText,      label: "Invoice" },
  room:        { icon: BedDouble,     label: "Room" },
};

function resultPath(r) {
  switch (r.type) {
    case "guest":       return `/pms/guests/${r.guest_id || r.id}`;
    case "reservation": return `/pms/reservations/${r.reservation_id || r.id}`;
    case "stay":        return `/pms/stays/${r.stay_id || r.id}`;
    case "folio":       return `/finance/folios/${r.folio_id || r.id}`;
    case "invoice":     return `/finance/folios`;
    case "room":        return `/inventory`;
    default:            return null;
  }
}

function useGlobalSearch() {
  const nav = useNavigate();
  const loc = useLocation();
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(0);

  useEffect(() => { setOpen(false); setQ(""); setResults([]); }, [loc.pathname]);

  // Debounced search (300ms)
  useEffect(() => {
    const term = q.trim();
    if (term.length < 1) { setResults([]); setOpen(false); setLoading(false); return; }
    setLoading(true);
    const hid = (document.cookie.split("; ").find(x => x.startsWith("active_hotel_id=")) || "").split("=")[1];
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/fo/search", { params: { q: term, ...(hid ? { hotel_id: decodeURIComponent(hid) } : {}) } });
        setResults(data.results || []); setActive(0); setOpen(true);
      } catch { setResults([]); setOpen(true); }
      finally { setLoading(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  const goTo = (r) => { const path = resultPath(r); if (!path) return; setOpen(false); setQ(""); setResults([]); nav(path); };
  const onInputKeyDown = (e) => {
    if (e.key === "Enter") { e.preventDefault(); if (results[active]) goTo(results[active]); }
    else if (e.key === "ArrowDown") { e.preventDefault(); setActive(a => Math.min(a + 1, results.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive(a => Math.max(a - 1, 0)); }
    else if (e.key === "Escape") setOpen(false);
  };
  return { q, setQ, results, open, setOpen, loading, active, setActive, goTo, onInputKeyDown };
}

function SearchResults({ s, testidPrefix = "topbar" }) {
  if (!(s.open && s.q.trim().length >= 1)) return null;
  return (
    <div className="absolute left-0 right-0 top-[calc(100%+8px)] max-h-[420px] overflow-y-auto rounded-xl border border-hocp-border bg-hocp-ghost shadow-2xl shadow-black/30 py-1.5 z-50" data-testid={`${testidPrefix}-search-results`}>
      {s.loading && !s.results.length && (
        <div className="px-4 py-6 text-center text-[13px] text-hocp-textTertiary flex items-center justify-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Searching…</div>)}
      {!s.loading && !s.results.length && (
        <div className="px-4 py-6 text-center text-[13px] text-hocp-textTertiary" data-testid={`${testidPrefix}-search-empty`}>No matches for “{s.q.trim()}”</div>)}
      {s.results.map((r, i) => {
        const meta = TYPE_META[r.type] || { icon: Search, label: r.type };
        const Icon = meta.icon;
        return (
          <button key={`${r.type}-${r.id}`} onMouseEnter={() => s.setActive(i)} onClick={() => s.goTo(r)}
            className={`w-full flex items-center gap-3 px-3 min-h-[44px] py-2 text-left transition-colors ${i === s.active ? "bg-hocp-alice/70" : "hover:bg-hocp-alice/40"}`} data-testid={`search-result-${i}`}>
            <span className="w-8 h-8 rounded-lg bg-hocp-alice/60 border border-hocp-border flex items-center justify-center shrink-0"><Icon className="w-4 h-4 text-hocp-textSecondary" /></span>
            <span className="min-w-0 flex-1">
              <span className="flex items-center gap-2">
                <span className="text-[13px] font-medium text-hocp-textPrimary truncate">{r.label}</span>
                {r.vip && <span className="text-[9px] uppercase font-bold px-1.5 py-0.5 rounded bg-[#FEF3C7] text-[#78530E]">VIP</span>}
              </span>
              {r.sublabel && <span className="block text-[11px] text-hocp-textTertiary truncate">{r.sublabel}</span>}
            </span>
            <span className="text-[10px] uppercase tracking-wider text-hocp-textTertiary shrink-0">{meta.label}</span>
          </button>
        );
      })}
    </div>
  );
}

/** Full-width search overlay for tablet + mobile (opened from the search icon). */
function SearchOverlay({ onClose }) {
  const s = useGlobalSearch();
  const ref = useRef(null);
  useEffect(() => { ref.current?.focus(); }, []);
  return (
    <div className="fixed inset-0 z-[1100] bg-[rgba(2,31,33,0.7)]" onClick={onClose} data-testid="search-overlay">
      <div className="bg-hocp-ghost p-3 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="relative flex items-center gap-2">
          <div className="relative flex-1">
            <Search size={20} strokeWidth={1.5} className="text-hocp-textDisabled absolute left-3 top-1/2 -translate-y-1/2 z-10" />
            <input ref={ref} className="hocp-topbar-search !text-[16px] !py-3" placeholder="Search reservations, guests, folios…" value={s.q}
              onChange={(e) => s.setQ(e.target.value)} onKeyDown={(e) => { if (e.key === "Escape") onClose(); else s.onInputKeyDown(e); }} autoComplete="off" data-testid="search-overlay-input" />
            <SearchResults s={s} testidPrefix="overlay" />
          </div>
          <button type="button" onClick={onClose} className="w-11 h-11 flex items-center justify-center rounded-lg text-hocp-textSecondary hover:bg-hocp-alice" data-testid="search-overlay-close" aria-label="Close search"><X size={20} strokeWidth={1.5} /></button>
        </div>
      </div>
    </div>
  );
}

function SearchIconButton({ className = "", testid }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className={`w-11 h-11 flex items-center justify-center rounded-lg ${className}`} data-testid={testid} aria-label="Search">
        <Search size={20} strokeWidth={1.5} />
      </button>
      {open && <SearchOverlay onClose={() => setOpen(false)} />}
    </>
  );
}

export default function Topbar() {
  const nav = useNavigate();
  const { user } = useAuth();
  const { bp } = useShell();
  const s = useGlobalSearch();
  const boxRef = useRef(null);
  const inputRef = useRef(null);
  const [dark, setDark] = useState(() => { try { return localStorage.getItem(THEME_KEY) === "dark"; } catch { return false; } });

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", dark ? "dark" : "light");
    try { localStorage.setItem(THEME_KEY, dark ? "dark" : "light"); } catch { /* ignore */ }
  }, [dark]);

  useEffect(() => {
    const onKey = (e) => { if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") { e.preventDefault(); inputRef.current?.focus(); } };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    const onClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) s.setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const initials = (user?.name || user?.email || "U").split(/[\s@._-]+/).filter(Boolean).slice(0, 2).map((x) => x[0].toUpperCase()).join("") || "U";

  return (
    <header className="sticky top-0 z-30 h-16 bg-hocp-ghost/85 backdrop-blur-xl border-b border-hocp-border" data-testid="topbar">
      <div className="h-full max-w-[1600px] mx-auto px-4 md:px-6 lg:px-8 flex items-center gap-3">
        <div className="hidden md:flex items-center gap-1.5 font-display font-bold text-[18px] text-hocp-textPrimary tracking-tight mr-2" data-testid="topbar-brand">HOCP<span className="w-1.5 h-1.5 rounded-full bg-hocp-mint inline-block" /></div>

        {bp === "desktop" ? (
          <div ref={boxRef} className="relative flex-1 max-w-2xl">
            <Search size={18} strokeWidth={1.5} className="text-hocp-textDisabled absolute left-4 top-1/2 -translate-y-1/2 z-10" />
            <input ref={inputRef} className="hocp-topbar-search" placeholder="Search reservations, guests, folios, payments…" value={s.q}
              onChange={(e) => s.setQ(e.target.value)} onKeyDown={s.onInputKeyDown} onFocus={() => { if (s.results.length) s.setOpen(true); }} autoComplete="off" data-testid="topbar-search" />
            <div className="hidden md:flex absolute right-3 top-1/2 -translate-y-1/2 items-center gap-1 text-[10px] font-semibold text-hocp-textTertiary bg-hocp-alice/60 border border-hocp-border rounded-md px-1.5 py-0.5">
              {s.loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Command className="w-3 h-3" />K</>}
            </div>
            <SearchResults s={s} />
          </div>
        ) : <div className="flex-1" />}

        <div className="flex items-center gap-1">
          {bp !== "desktop" && <SearchIconButton className="text-hocp-textSecondary hover:bg-hocp-alice" testid="topbar-search-icon" />}
          <button className="w-11 h-11 flex items-center justify-center rounded-lg text-hocp-textSecondary hover:bg-hocp-alice" onClick={() => setDark(v => !v)} title={dark ? "Light mode" : "Dark mode"} data-testid="theme-toggle" aria-label="Toggle theme">
            {dark ? <Sun size={20} strokeWidth={1.5} /> : <Moon size={20} strokeWidth={1.5} />}
          </button>
          <button className="w-11 h-11 flex items-center justify-center rounded-lg text-hocp-textSecondary hover:bg-hocp-alice relative" data-testid="topbar-notifications" onClick={() => nav("/notifications")} aria-label="Notifications">
            <Bell size={20} strokeWidth={1.5} />
            <span className="absolute top-2.5 right-2.5 w-1.5 h-1.5 bg-[#DC2626] rounded-full" />
          </button>
          <div className="w-px h-6 bg-hocp-border mx-1" />
          <button className="flex items-center gap-2 pr-2 pl-1 min-h-[44px] rounded-full hover:bg-hocp-alice/60 transition-colors" onClick={() => nav("/profile")} data-testid="topbar-profile" aria-label="Profile">
            <div className="w-8 h-8 rounded-full bg-hocp-deepTeal text-white flex items-center justify-center font-semibold text-[12px]" data-testid="topbar-avatar">{initials}</div>
            <div className="hidden lg:block text-left leading-tight">
              <div className="text-[13px] font-semibold text-hocp-textPrimary max-w-[140px] truncate">{user?.name || user?.email || "User"}</div>
              <div className="text-[11px] text-hocp-textTertiary uppercase tracking-wider">{user?.role || "operator"}</div>
            </div>
            <ChevronDown className="w-3.5 h-3.5 text-hocp-textTertiary hidden lg:block" />
          </button>
        </div>
      </div>
    </header>
  );
}

Topbar.MobileSearch = function MobileSearch() {
  return <SearchIconButton className="text-hocp-softIce hover:bg-hocp-deepTeal" testid="mobile-search" />;
};
